import { type MouseEvent, type PropsWithChildren, memo } from "react";

import { type TTailwindString, tw } from "classnames";

type LinkType = "primary" | "secondary" | "tertiary";
type LinkTextSize = "sm" | "md" | "lg";

interface Props {
  className?: string;
  external?: boolean;
  mtrAs?: string;
  mtrTarget?: string;
  mtrTrack?: "click" | false;
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
  onMouseEnter?: (e: MouseEvent<HTMLAnchorElement>) => void;
  size?: LinkTextSize;
  to: string;
  type?: LinkType;
  underline?: boolean;
  unstyled?: boolean;
}

const styles = {
  link: ({
    size,
    type,
    underline,
  }: Pick<Props, "type" | "underline" | "size">) =>
    tw(
      "font-calibre",
      "antialiased",
      {
        [tw(
          "text-interactive-brick",
          "hover:text-interactive-brick-hover" as TTailwindString,
          "active:text-interactive-brick-pressed" as TTailwindString
        )]: type === "primary",
        [tw(
          "text-button-secondary",
          "hover:text-button-secondary-hover" as TTailwindString,
          "active:text-button-secondary-pressed" as TTailwindString
        )]: type === "secondary",
        [tw(
          "text-interactive-white",
          "hover:text-interactive-white-hover" as TTailwindString,
          "active:text-interactive-white-pressed" as TTailwindString
        )]: type === "tertiary",
      },
      {
        [tw("underline", "decoration-1" as TTailwindString)]: underline,
        [tw("no-underline", "hover:underline")]: !underline,
      },
      {
        [tw("text-xs")]: size === "sm",
        [tw("text-lg")]: size === "lg",
        [tw("text-base")]: !size || size === "md",
      }
    ),
};

const LinkBase = ({
  children,
  className,
  external,
  mtrAs = "clickModule",
  mtrTarget = "link",
  mtrTrack = "click",
  onClick,
  onMouseEnter,
  size = "md",
  to,
  type = "primary",
  underline,
  unstyled = false,
}: PropsWithChildren<Props>) => {
  const linkClasses = styles.link({ type, underline, size });

  const classNames = tw(
    linkClasses,
    className
      ? {
          [tw(className as TTailwindString)]: className !== undefined,
        }
      : undefined
  );

  const rel = external ? "noopener noreferrer nofollow" : undefined;
  const target = external ? "_blank" : undefined;
  const ariaLabel = external
    ? `${children} – link opens in a new tab`
    : undefined;

  const maestroProps = mtrTrack
    ? {
        "data-mtr-as": mtrAs,
        "data-mtr-canonical-href": to,
        "data-mtr-target": mtrTarget,
        "data-mtr-track": mtrTrack,
      }
    : {};

  return (
    <a
      aria-label={ariaLabel}
      className={unstyled ? className : classNames}
      href={to}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      rel={rel}
      target={target}
      {...maestroProps}
    >
      {children}
    </a>
  );
};

export const Link = memo(LinkBase);
